/**
 * Maps raw trial-enrollment dataset rows into the normalized
 * OnboardingMonthRow[] the charts use.
 */
import type {
  OnboardingMonthRow,
  ScenarioPoint,
} from "./onboardingPlots.types";

type Raw = Record<string, unknown>;

function num(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (s === "" || s === "--") return null;
  const n = Number(s.replace("%", ""));
  return Number.isNaN(n) ? null : n;
}

// Rate fields in the dataset are expressed as percentages (e.g. 20 for 20%);
// the charts expect fractions (0-1).
function percentToFraction(v: unknown): number | null {
  const n = num(v);
  return n === null ? null : n / 100;
}

function scenario(r: Raw, screenKey: string, enrollKey: string): ScenarioPoint {
  return {
    screenedCum: num(r[screenKey]),
    enrolledCum: num(r[enrollKey]),
  };
}

const MONTH_ABBR: Record<string, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

// `Month_derived` is a study-relative ordinal (month 1 = trial start), not a
// calendar month, so it can't be combined with `Year_derived` to get a real
// date. The `Date` field (e.g. "Dec-25") is the actual calendar month —
// parse that instead, returning the ISO date of that month's last day.
function parseDatasetMonth(raw: unknown): string {
  const match = /^([A-Za-z]{3})-(\d{2})$/.exec(String(raw ?? "").trim());
  if (!match) return "";
  const monthIndex = MONTH_ABBR[match[1]];
  if (monthIndex === undefined) return "";
  const year = 2000 + Number(match[2]);
  return new Date(Date.UTC(year, monthIndex + 1, 0)).toISOString().slice(0, 10);
}

export function mapOnboardingRows(rows: Raw[]): OnboardingMonthRow[] {
  // The API doesn't guarantee chronological order, but every chart draws
  // lines/bars in array order (not sorted by x-value), so an unsorted
  // response makes the charts zigzag nonsensically.
  const sorted = [...rows].sort((a, b) =>
    parseDatasetMonth(a["Date"]).localeCompare(parseDatasetMonth(b["Date"])),
  );

  return sorted.map((r) => {
    const monthNum = num(r["Month_derived"]) ?? 0;

    return {
      date: parseDatasetMonth(r["Date"]),
      monthNum,

      contractedActiveSites: num(r["Contracted_Active_Sites_derived"]),
      totalActiveSites: num(r["Actual_Total_Active_Sites_derived"]),

      screenedPerMonth: num(r["Actual_Screened_per_Month_derived"]),
      screenFailsPerMonth: num(r["Actual_Screen_Fails_per_Month_derived"]),
      screenFailRatePerMonth: percentToFraction(
        r["Actual_Screen_Fail_Rate_per_Month_derived"],
      ),
      enrolledPerMonth: num(r["Actual_Enrolled_per_Month_derived"]),
      enrolledPerSitePerMonth: num(r["Actual_Enrolled/_Site/Month_derived"]),

      screenedCumulative: num(r["Actual_Screened_Cumulative_derived"]),
      screenFailsCumulative: num(r["Actual_Screen_Fails_Cumulative_derived"]),
      screenFailRateCumulative: percentToFraction(
        r["Actual_Screen_Fail_Rate_Cumulative_derived"],
      ),
      enrolledCumulative: num(r["Actual_Enrolled_Cumulative_derived"]),
      enrolledPerSiteCumulative: num(
        r["Actual_Enrolled/_Site/Month_Cumulative_derived"],
      ),
      forecastEnrolledCumulativeP10: num(
        r["Forecast_Enrolled_Cumulative_p10_derived"],
      ),
      forecastEnrolledCumulativeP50: num(
        r["Forecast_Enrolled_Cumulative_p50_derived"],
      ),
      forecastEnrolledCumulativeP90: num(
        r["Forecast_Enrolled_Cumulative_p90_derived"],
      ),

      scenarios: {
        stretch: scenario(
          r,
          "Stretch_Goal:_0.8_Screen_Cumulative_derived",
          "Stretch_Goal:_0.8_Enroll_Cumulative_derived",
        ),
        target: scenario(
          r,
          "Target_Rate:_0.7_Screen_Cumulative_derived",
          "Target_Rate:_0.7_Enroll_Cumulative_derived",
        ),
        baseline: scenario(
          r,
          "Baseline_Rate:_0.6_Screen_Cumulative_derived",
          "Baseline_Rate:_0.6_Enroll_Cumulative_derived",
        ),
        minimum: scenario(
          r,
          "cf_:_0.5_Screen_Cumulative_derived",
          "cf_:_0.5_Enroll_Cumulative_derived",
        ),
      },
    };
  });
}
